import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    InputAdornment,
    CircularProgress,
    Tabs,
    Tab,
    Alert,
    Grid,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useTranslate, useNotify } from 'react-admin';
import {
    LibraryItem,
    LibraryItemType,
    LibraryReferenceMode,
    libraryService,
} from '../../services/libraryService';
import LibraryItemCard from './LibraryItemCard';
import InsertFromLibraryDialog from './InsertFromLibraryDialog';

interface SelectFromLibraryDialogProps {
    open: boolean;
    onClose: () => void;
    itemType: LibraryItemType;
    targetId: number; // catalogId for courses, courseId for lessons
    onInserted: () => void;
}

const SelectFromLibraryDialog: React.FC<SelectFromLibraryDialogProps> = ({
    open,
    onClose,
    itemType,
    targetId,
    onInserted,
}) => {
    const [items, setItems] = useState<LibraryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [tab, setTab] = useState(0); // 0 = company, 1 = operator
    const [selectedItem, setSelectedItem] = useState<LibraryItem | null>(null);
    const [insertDialogOpen, setInsertDialogOpen] = useState(false);
    const [inserting, setInserting] = useState(false);

    const translate = useTranslate();
    const notify = useNotify();

    useEffect(() => {
        if (open) {
            loadItems();
        }
    }, [open, tab, search]);

    const loadItems = async () => {
        setLoading(true);
        try {
            const allItems = await libraryService.getLibraryItems({
                type: itemType,
                search: search || undefined,
                operatorOnly: tab === 1,
            });

            // Filter based on tab
            const filtered = tab === 0
                ? allItems.filter(item => !item.isOperatorContent)
                : allItems.filter(item => item.isOperatorContent);

            setItems(filtered);
        } catch (error) {
            notify(translate('library.loadError'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSelectItem = (item: LibraryItem) => {
        setSelectedItem(item);
        setInsertDialogOpen(true);
    };

    const handleInsertConfirm = async (mode: LibraryReferenceMode) => {
        if (!selectedItem) return;

        setInserting(true);
        try {
            if (itemType === 'course') {
                const result = await libraryService.insertCourseFromLibrary(selectedItem.id, {
                    catalogId: targetId,
                    mode,
                });
                notify(
                    translate('library.insertDialog.courseCreated', {
                        title: result.course.title,
                        mode: mode === 'copy'
                            ? translate('library.insertDialog.asCopy')
                            : translate('library.insertDialog.asReference'),
                    }),
                    { type: 'success' }
                );
            } else {
                const result = await libraryService.insertLessonFromLibrary(selectedItem.id, {
                    courseId: targetId,
                    mode,
                });
                notify(
                    translate('library.insertDialog.lessonCreated', {
                        title: result.lesson.title,
                        mode: mode === 'copy'
                            ? translate('library.insertDialog.asCopy')
                            : translate('library.insertDialog.asReference'),
                    }),
                    { type: 'success' }
                );
            }

            setInsertDialogOpen(false);
            setSelectedItem(null);
            onInserted();
            onClose();
        } catch (error: any) {
            const message = error?.response?.data?.error || translate('library.insertError');
            notify(message, { type: 'error' });
        } finally {
            setInserting(false);
        }
    };

    const handleClose = () => {
        setSearch('');
        setTab(0);
        setSelectedItem(null);
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
                <DialogTitle>
                    {itemType === 'course'
                        ? translate('library.selectDialog.titleCourse')
                        : translate('library.selectDialog.titleLesson')}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 2 }}>
                        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                            <Tab label={translate('library.tabs.company')} />
                            <Tab label={translate('library.tabs.operator')} />
                        </Tabs>
                    </Box>

                    <TextField
                        fullWidth
                        placeholder={translate('library.search')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 3 }}
                    />

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : items.length === 0 ? (
                        <Alert severity="info">
                            {translate('library.noItems')}
                        </Alert>
                    ) : (
                        <Grid container spacing={2}>
                            {items.map((item) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id}>
                                    <LibraryItemCard
                                        item={item}
                                        onClick={() => handleSelectItem(item)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>
                        {translate('ra.action.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>

            {selectedItem && (
                <InsertFromLibraryDialog
                    open={insertDialogOpen}
                    onClose={() => {
                        setInsertDialogOpen(false);
                        setSelectedItem(null);
                    }}
                    item={selectedItem}
                    onConfirm={handleInsertConfirm}
                />
            )}

            {/* Loading overlay during insert */}
            {inserting && (
                <Dialog open={true} PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none' } }}>
                    <CircularProgress sx={{ color: 'white' }} />
                </Dialog>
            )}
        </>
    );
};

export default SelectFromLibraryDialog;
